/**
 * @domino/types — canonical shared data contracts for Egyptian Domino.
 *
 * This package is the single source of truth for game-level types shared by
 * the rules engine, the Convex backend, the bots, and the client.
 *
 * Conventions (see egyptian-domino-build-guide.md):
 * - Tile ids are 0–27; the pair is stored in canonical order (higher number first).
 * - Events never carry private information (drawn tile identities, boneyard order).
 * - All rule variants live in a frozen RulesSnapshot stored on the match.
 */

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

/** Tile id in the double-six set (0–27). */
export type TileId = number;

/** A seat at the table. 0-based; 2v2 uses 0–3, 3P uses 0–2, 1v1 uses 0–1. */
export type SeatIndex = number;

/** Team id. 2v2: seats 0 & 2 = team 0, seats 1 & 3 = team 1. */
export type TeamId = 0 | 1;

/** An open end of the chain. The first placement of a round uses "center". */
export type End = "left" | "right" | "center";

export type GameMode = "1v1" | "3p" | "2v2";

/** Number of seats for a mode. */
export const SEAT_COUNT: Record<GameMode, 2 | 3 | 4> = {
  "1v1": 2,
  "3p": 3,
  "2v2": 4,
};

// ---------------------------------------------------------------------------
// Tile
// ---------------------------------------------------------------------------

export interface Tile {
  /** Unique id 0–27. All logic compares by id. */
  readonly id: TileId;
  /** Higher half of the pair (canonical order). */
  readonly high: number;
  /** Lower half of the pair. */
  readonly low: number;
  /** True when high === low. */
  readonly isDouble: boolean;
  /** high + low (blank = 0). Total across the set is 168. */
  readonly pipValue: number;
}

// ---------------------------------------------------------------------------
// Rules snapshot (frozen at match start)
// ---------------------------------------------------------------------------

/** How the boneyard behaves (draw modes) or is set aside (no-draw modes). */
export type BoneyardMode = "draw" | "none";

/** Play direction. Default counter-clockwise (D-01). */
export type PlayDirection = "clockwise" | "counter-clockwise";

/** Block scoring policy (D-04). */
export type BlockScoring = "opponents-total" | "difference";

/** Tied-block handling (D-05). */
export type TiedBlockPolicy = "no-score" | "carry-over";

/** Pass handling (D-10). */
export type PassMode = "automatic" | "manual";

/** Round 2+ opener policy (D-03). */
export type OpenerPolicy = "winner" | "loser";

export interface BonusFlags {
  /** Going out with a double. */
  doubleOut: boolean;
  /** Opponents hold all tiles unplayed. */
  shutout: boolean;
  /** Bonus for the blocker. */
  blocker: boolean;
}

/**
 * Frozen rules snapshot stored on the match at creation.
 * Deploys never change the rules of a game in progress (S-17).
 */
export interface RulesSnapshot {
  /** Rules variant key; extensible for future variants. */
  variant: string;
  mode: GameMode;
  seatCount: 2 | 3 | 4;
  /** Tiles dealt per seat: 7 default, 9 for the 3P alternative. */
  handSize: number;
  boneyard: BoneyardMode;
  /** Draw until empty (0) or stop when this many remain (e.g. 2). */
  boneyardFloor: number;
  playDirection: PlayDirection;
  /** Round 1: opener must play the highest double (D-02, default ON). */
  openerMustPlayHighestDouble: boolean;
  /** Round 2+ opener policy (D-03). */
  roundOpenerPolicy: OpenerPolicy;
  blockScoring: BlockScoring;
  tiedBlock: TiedBlockPolicy;
  /** First side to reach or exceed wins (D-06, default 101). */
  targetScore: number;
  passMode: PassMode;
  /** Redeal when a hand holds 5+ doubles (D-12, default OFF). */
  redealOnFiveDoubles: boolean;
  /** Cap on redeals per round to guarantee termination. */
  maxRedeals: number;
  /** Turn timer seconds (D-13, default 30). */
  turnTimerSeconds: number;
  /** Time bank per player per match (D-13, default 60). */
  timeBankSeconds: number;
  /** Consecutive timeouts before bot takeover (D-14, default 2). */
  timeoutsBeforeBotTakeover: number;
  /** Disconnect grace seconds (D-15, default 45). */
  disconnectGraceSeconds: number;
  bonuses: BonusFlags;
  /** Version of the rules definition; bumped on rule-shape changes. */
  rulesVersion: string;
}

/**
 * Build a rules snapshot for a mode from the recommended defaults (Section 10),
 * applying optional overrides. Called at match start; the result is frozen.
 */
export function makeRulesSnapshot(
  mode: GameMode,
  overrides: Partial<Omit<RulesSnapshot, "mode" | "seatCount" | "rulesVersion">> = {},
): RulesSnapshot {
  const seatCount = SEAT_COUNT[mode];
  const base: RulesSnapshot = {
    variant: "classic",
    mode,
    seatCount,
    handSize: 7,
    boneyard: mode === "2v2" ? "none" : "draw",
    boneyardFloor: 0,
    playDirection: "counter-clockwise",
    openerMustPlayHighestDouble: true,
    roundOpenerPolicy: "winner",
    blockScoring: "opponents-total",
    tiedBlock: "no-score",
    targetScore: 100,
    passMode: "automatic",
    redealOnFiveDoubles: false,
    maxRedeals: 3,
    turnTimerSeconds: 30,
    timeBankSeconds: 60,
    timeoutsBeforeBotTakeover: 2,
    disconnectGraceSeconds: 45,
    bonuses: { doubleOut: false, shutout: false, blocker: false },
    rulesVersion: "1.0.0",
  };
  return { ...base, ...overrides, mode, seatCount };
}

// ---------------------------------------------------------------------------
// Chain
// ---------------------------------------------------------------------------

/** One placed tile in the chain. Geometry is derived client-side; server stores no coordinates. */
export interface ChainPlacement {
  tileId: TileId;
  /** Which side this tile was added from; "center" for the first tile. */
  side: End;
  /** Which half faces inward (toward the chain) / outward. Equal for doubles. */
  inwardHalf: number;
  outwardHalf: number;
}

export interface Chain {
  /** Ordered from leftmost to rightmost placement. */
  placements: ChainPlacement[];
  /** Open end values; null while the chain is empty. */
  leftEnd: number | null;
  rightEnd: number | null;
}

// ---------------------------------------------------------------------------
// Legal moves
// ---------------------------------------------------------------------------

/**
 * A legal move for a seat: a tile plus the ends it may be played on.
 * Orientation is never a player choice — it is determined by which half matches.
 */
export interface LegalMove {
  tileId: TileId;
  ends: End[];
}

// ---------------------------------------------------------------------------
// Events (append-only public log)
// ---------------------------------------------------------------------------

export type GameEventType =
  | "round-started"
  | "deal"
  | "opener"
  | "play"
  | "draw"
  | "pass"
  | "domino"
  | "block"
  | "redeal"
  | "round-ended"
  | "match-ended"
  | "forfeit"
  | "timeout"
  | "seat-away"
  | "bot-takeover"
  | "seat-takeback";

/**
 * A public event. Events NEVER carry private tile identity: a "draw" event
 * says who drew and the new boneyard count; a "play" event carries the tile
 * because it is public the moment it is placed.
 */
export interface GameEvent {
  /** Monotonic sequence number within the round. */
  seq: number;
  type: GameEventType;
  /** Seat the event concerns, when applicable. */
  seat?: SeatIndex;
  /** Public only for play events. */
  tileId?: TileId;
  end?: End;
  /** Boneyard count after the event (draw events). */
  boneyardCount?: number;
  data?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Round state (full, server-side) and views
// ---------------------------------------------------------------------------

export type RoundStatus = "dealing" | "active" | "ended";

export type RoundEndReason = "domino" | "block" | "forfeit" | "abandoned";

/** Itemized bonus line so the UI can itemize scoring. */
export interface BonusLine {
  key: string;
  points: number;
  seat: SeatIndex;
}

export interface RoundResult {
  reason: RoundEndReason;
  /** Winning seat indices (empty on tied block / no winner). */
  winners: SeatIndex[];
  /** Winning side (team id in 2v2; seat index in 1v1/3P); null when tied. */
  winningSide: number | null;
  /** Points awarded to the winning side (0 when none). */
  points: number;
  /** Pip count per seat, keyed by seat index. */
  pipCounts: Record<string, number>;
  bonuses: BonusLine[];
  /** True when the block was tied (opener rotates). */
  tied: boolean;
}

/**
 * Full server-side round state. Lives only on the server (Convex private docs
 * + engine); never sent to clients in full.
 */
export interface RoundState {
  roundNumber: number;
  rules: RulesSnapshot;
  /** Tiles held per seat (tile ids). */
  hands: TileId[][];
  /** Ordered boneyard (tile ids), hidden. */
  boneyard: TileId[];
  chain: Chain;
  currentSeat: SeatIndex;
  /** Monotonic turn number for stale-proofing (S-01/S-05). */
  turnNumber: number;
  /** Turn number during which the current seat drew, to enforce max 1 draw per turn. */
  lastDrawnTurnNumber?: number;
  status: RoundStatus;
  endReason?: RoundEndReason;
  result?: RoundResult;
  /** Consecutive passes (resets on play). */
  passStreak: number;
  /** Redeals used this round (cap maxRedeals). */
  redealsUsed: number;
  /** Stored shuffle seed; private until round end (S-31). */
  seed: number;
  /** Public event log for this round. */
  events: GameEvent[];
  openerSeat: SeatIndex;
}

/**
 * Player-visible projection: own hand, others as counts, boneyard as count,
 * public events. The projection from RoundState is the only path to clients.
 */
export interface PlayerView {
  roundNumber: number;
  status: RoundStatus;
  mySeat: SeatIndex;
  /** My own hand (full tiles). */
  myHand: Tile[];
  /** Hand size per seat (counts only for others). */
  handCounts: number[];
  boneyardCount: number;
  chain: Chain;
  currentSeat: SeatIndex;
  turnNumber: number;
  /** Legal moves for my seat. */
  legalMoves: LegalMove[];
  /** Whether the current seat may draw a tile from the boneyard. */
  canDraw?: boolean;
  /** Whether the current seat may pass their turn (when no legal move exists). */
  canPass?: boolean;
  /** Public events. */
  events: GameEvent[];
  result?: RoundResult;
}

// ---------------------------------------------------------------------------
// Match state
// ---------------------------------------------------------------------------

export type MatchStatus = "lobby" | "active" | "finished";

export interface MatchState {
  mode: GameMode;
  seatCount: 2 | 3 | 4;
  /** Score per side: team 0/1 in 2v2; seat 0..n-1 in 1v1/3P. */
  scores: number[];
  targetScore: number;
  roundNumber: number;
  status: MatchStatus;
  /** Winning side at match end; null while active. */
  winner: number | null;
}

// ---------------------------------------------------------------------------
// Rejection reasons (stable enum, used by server and UI)
// ---------------------------------------------------------------------------

export const RejectionReason = {
  NotYourTurn: "not-your-turn",
  TileNotInHand: "tile-not-in-hand",
  TileDoesNotMatchEnd: "tile-does-not-match-end",
  EndRequiredButMissing: "end-required-but-missing",
  PassNotAllowed: "pass-not-allowed",
  DrawNotAllowed: "draw-not-allowed",
  RoundNotActive: "round-not-active",
  StaleTurn: "stale-turn",
  SeatNotControlled: "seat-not-controlled",
} as const;

export type RejectionReason = (typeof RejectionReason)[keyof typeof RejectionReason];

export interface Rejection {
  readonly reason: RejectionReason;
  readonly detail?: string;
}

/** Result of an engine apply call: new state plus emitted events. */
export interface ApplyResult {
  readonly state: RoundState;
  readonly events: GameEvent[];
}

// ---------------------------------------------------------------------------
// Seats, bots, presence
// ---------------------------------------------------------------------------

export type BotDifficulty = "easy" | "normal" | "hard";

export type ConnectionState = "connected" | "disconnected" | "away";

export type ControlledBy = "human" | "bot" | "autopilot";

// ---------------------------------------------------------------------------
// Team helpers
// ---------------------------------------------------------------------------

/** Team for a seat in 2v2 (partners sit opposite: 0&2 vs 1&3); null in other modes. */
export function teamForSeat(mode: GameMode, seat: SeatIndex): TeamId | null {
  if (mode !== "2v2") return null;
  return seat % 2 === 0 ? 0 : 1;
}

/** Seats belonging to a side (team in 2v2; the seat itself in 1v1/3P). */
export function seatsForSide(mode: GameMode, side: number): SeatIndex[] {
  if (mode === "2v2") {
    return [0, 1, 2, 3].filter((s) => (s % 2 === 0 ? 0 : 1) === side);
  }
  return [side];
}

/** Opposing sides of a side (all other sides). */
export function opposingSides(mode: GameMode, side: number): number[] {
  const total = mode === "3p" ? 3 : 2;
  return Array.from({ length: total }, (_, i) => i).filter((s) => s !== side);
}
